"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Mail, FileText, CircleCheck as CheckCircle2, Lock, ArrowRight, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const industryOptions = ["Financial Services & Banking", "Technology & Enterprise Software", "Healthcare & Life Sciences", "Industrial & Manufacturing", "Energy & Clean Tech", "Professional Services & Legal", "Media & Telecommunications", "Other"];
const editorialStandards = [{
  title: "Zero sponsored placement",
  description: "Every analytical summary is produced independently by our research staff."
}, {
  title: "Weekly curated dispatch",
  description: "One comprehensive briefing sent every Thursday morning at 6:00 AM EST."
}, {
  title: "Strict data privacy",
  description: "Your corporate credentials are never rented, sold, or shared with third parties."
}];
function SubscribeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-border bg-muted/40 p-8 text-center sm:p-10">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="font-serif text-2xl font-semibold text-foreground">
          Subscription confirmed
        </h3>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">
          {successMessage || "Thank you. Your corporate subscription to the MinfoMedia Executive Briefing is active. The next issue will arrive this Thursday."}
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto">
            Manage preferences
          </Button>
          <Button asChild className="w-full sm:w-auto">
            <Link to="/ResearchReportsPage">
              Browse current reports
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters required"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
                First Name
              </FormLabel>
              <FormControl>
                <Input placeholder="First name" autoComplete="given-name" className="h-11 rounded-sm border-input bg-card px-3.5 text-sm text-foreground focus-visible:border-ring focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Work Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="work@company.com" autoComplete="email" className="h-11 rounded-sm border-input bg-card px-3.5 text-sm text-foreground focus-visible:border-ring focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="industry" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Primary Industry <span className="font-normal text-muted-foreground">(Optional)</span>
            </FormLabel>
            <FormControl>
              <select {...field} className="flex h-11 w-full rounded-sm border border-input bg-card px-3.5 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50">
                <option value="">Select industry</option>
                {industryOptions.map((ind, i) => <option key={ind} value={ind} data-index={i}>
                    {ind}
                  </option>)}
              </select>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Used to tailor analytical sector briefings to your operational domain.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "You must accept the briefing terms and privacy policy"
    }} render={({
      field
    }) => <FormItem className="space-y-0 rounded-sm border border-border/80 bg-muted/30 p-3.5">
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-0.5" />
              </FormControl>
              <div className="space-y-1 leading-tight">
                <FormLabel className="text-xs font-normal leading-relaxed text-foreground cursor-pointer">
                  I agree to receive the weekly MinfoMedia briefing and accept the{" "}
                  <Link to="/PrivacyDataProtectionPage" className="font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground">
                    privacy policy
                  </Link>
                  .
                </FormLabel>
              </div>
            </div>
            <FormMessage className="mt-2" />
          </FormItem>} />

      {submitError && <div className="rounded-sm border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-sm bg-primary px-6 text-sm font-semibold tracking-wide text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 focus-visible:ring-ring">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Authorizing subscription...
          </> : <>
            Subscribe to Executive Briefing
            <ArrowRight className="size-4" />
          </>}
      </Button>

      <div className="flex items-center justify-center gap-2 pt-1 text-center text-xs text-muted-foreground">
        <Lock className="size-3.5" />
        <span>Strict privacy standards. Single-click unsubscribe anytime.</span>
      </div>
    </Form>;
}
export default function Subscribe() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="subscribe" data-nav="light" className="relative border-b border-border bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead Broadsheet Divider Rule */}
        <div className="mb-10 sm:mb-12">
          <div className="h-[1px] w-full bg-foreground" />
          <div className="mt-1 h-[0.5px] w-full bg-border" />
        </div>

        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left Column: Context & Editorial Assurance */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 12
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 lg:pr-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              <Mail className="size-3.5 text-accent" />
              <span>Institutional Publication</span>
            </div>

            <h1 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-[2.5rem] lg:leading-[1.18]">
              The weekly executive briefing.
            </h1>

            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Curated research summaries, data analysis, and peer-reviewed market insights delivered to your inbox every Thursday morning.
            </p>

            {/* Editorial Commitment List */}
            <div className="mt-8 space-y-4 border-t border-border pt-6">
              {editorialStandards.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
                    <CheckCircle2 className="size-3.5 text-foreground" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h2>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Institutional Compliance Notice */}
            <div className="mt-8 flex items-center gap-3 rounded-md border border-border bg-card p-4 shadow-2xs">
              <ShieldCheck className="size-5 shrink-0 text-foreground" />
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">GDPR & Global Compliance.</span>{" "}
                Read our full commitment in the{" "}
                <Link to="/PrivacyDataProtectionPage" className="font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground">
                  Privacy & Data Protection charter
                </Link>
                .
              </div>
            </div>
          </motion.div>

          {/* Right Column: Embedded Subscription Form Card */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.55,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-7">
            <Card className="rounded-md border border-border bg-card p-6 shadow-md sm:p-8">
              <CardHeader className="p-0 pb-6 border-b border-border/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-normal text-muted-foreground">
                    <FileText className="size-3.5 text-muted-foreground" />
                    <span>ISSN: 2840-192X</span>
                  </div>
                  <span className="rounded-sm border border-border bg-background px-2 py-0.5 text-[11px] font-mono text-muted-foreground">
                    Vol. 2025 Ed.
                  </span>
                </div>
                <CardTitle className="mt-3 font-serif text-2xl font-semibold tracking-tight text-foreground">
                  Executive Briefing Subscription
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Join over 34,000 corporate strategists, analysts, and enterprise leaders.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-0 pt-6">
                <FormProvider formId="newsletter_subscribe_form" sectionName="subscribe-section" defaultValues={{
                first_name: "",
                email: "",
                industry: "",
                consent: false
              }} validationRules={{
                first_name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                consent: {
                  required: true
                }
              }} submitText="Subscribe to Executive Briefing" successMessage="Thank you. Your corporate subscription to the MinfoMedia Executive Briefing is active. The next issue will arrive this Thursday." errorMessage="Unable to process subscription. Please check your details and try again.">
                  <SubscribeForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}